'use client';

import cn from 'classnames';
import { SelectHTMLAttributes, forwardRef, useId } from 'react';
import Icon from '../Icon';
import { Text } from '../Text';

export type SelectFieldOption = {
  label: string;
  value: string;
};

export type SelectFieldProps
  = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> & {
    label?: string;
    options: SelectFieldOption[];
    placeholder?: string;
    errorMessage?: string;
    wrapperClassName?: string;
  }

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  (
    {
      label,
      options,
      placeholder,
      errorMessage,
      wrapperClassName,
      className,
      value,
      ...selectProps
    },
    ref,
  ) => {
    const id = useId();

    return (
      <label htmlFor={id} className={cn('flex flex-col gap-2xs', wrapperClassName)}>
        {label && (
          <Text as="span" type="p2" className="text-primary">
            {label}
          </Text>
        )}
        <div className="relative group">
          <select
            ref={ref}
            id={id}
            value={value ?? ''}
            className={cn(
              'appearance-none w-full surface-l1 text-primary',
              'rounded-[6px] pl-s pr-6 py-xs',
              'font-sans font-size-p2 font-line-height-p2',
              'outline-none focus-visible:ring-2 focus-visible:ring-citron-500',
              className,
            )}
            {...selectProps}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Icon
            name="chevron-down"
            size={20}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-primary pointer-events-none group-hover:icon-action-active"
          />
        </div>
        {errorMessage && (
          <Text as="span" type="caption2" className="text-red-500">
            {errorMessage}
          </Text>
        )}
      </label>
    );
  },
);

SelectField.displayName = 'SelectField';

export default SelectField;
